//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { SessionMode } from 'app/moderation-mode/constants/SessionMode';
import { resolveSessionMode } from 'app/moderation-mode/moderationModeResolver';

type SessionModeListener = () => void;

/**
 * The active view mode for render-time consumers (React and Angular), kept in a
 * focused external store rather than Redux. localStorage remains the persisted
 * source of truth and resolveSessionMode the synchronous pre-mount read
 * for route guards; this store is the live value React subscribes to.
 */
const createSessionModeStore = () => {
  const listeners = new Set<SessionModeListener>();
  let mode: SessionMode = resolveSessionMode();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const getMode = (): SessionMode => mode;

  const setMode = (nextMode: SessionMode) => {
    if (nextMode === mode) {
      return;
    }

    mode = nextMode;
    notify();
  };

  // Re-resolve from the live session and persisted mode. Called once the
  // session settles so the store reflects the authoritative value.
  const hydrate = () => {
    setMode(resolveSessionMode());
  };

  const subscribe = (listener: SessionModeListener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
  };

  return { getMode, hydrate, setMode, subscribe };
};

export const sessionModeStore = createSessionModeStore();
